/**
 * "Is this item still processing?" — the ONE answer, for the client and the server (MAR-948).
 *
 * Built on the shared status → state mapping (`shared/indexing/indexState.ts`) rather than on a list
 * of its own. It replaced seven copies of `status === 'processing' || status === 'uploading'` and
 * three wider checks that embedded the same pair; drift between such copies is what reported a
 * `quota_blocked` upload as ready (MAR-957). `tests/unit/itemPendingGuard.test.ts` keeps it at one.
 */
import { legacyFromStatus } from '../indexing/indexState'

/** `items.index_state` values that mean indexing is still in flight — for SQL filters. */
export const PENDING_INDEX_STATES = ['queued', 'working'] as const

/** Pre-0071 `items.status` values that mean the same thing — for SQL filters over legacy rows. */
export const PENDING_ITEM_STATUSES = ['uploading', 'processing'] as const

/**
 * Is an indexing STATE still in flight? Accepts the browser's `uploading` too: an upload with no
 * server row yet is what `legacyFromStatus` calls `queued`.
 */
export function isIndexStatePending(state: string | null | undefined): boolean {
  return state === 'uploading' || (PENDING_INDEX_STATES as readonly string[]).includes(state ?? '')
}

/** Is an item, known only by its `status`, still uploading or being indexed? */
export function isItemPending(status: string | null | undefined): boolean {
  return isIndexStatePending(legacyFromStatus(status ?? null))
}

/**
 * Has an item, known only by its `status`, finished indexing successfully? False while it is pending,
 * and also when it failed (`error`) or is waiting on quota (`quota_blocked`) — none of those has
 * readable text. `draft`, `uploaded` and an absent status are settled.
 */
export function isItemIndexed(status: string | null | undefined): boolean {
  return legacyFromStatus(status ?? null) === 'ready'
}
